import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { Patient, PatientForm, PatientSearchForm } from 'entities/patient'

type PatientsResponse = PagingResponse & {
    results: Patient[]
}

export function useFetchPatientsApi(searchForm: Form<PatientSearchForm>): IndexApiSet<PatientsResponse> & { execute: () => void } {
    const apiPath = 'patients/'
    const api = useIndexApi<PatientsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm.object.page || 1, perPage: searchForm.object.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm.object })
    }

    useEffectSkipFirst(() => {
        searchForm.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm.object])

    return { ...api, execute: execute }
}

export function useDownloadCsvPatientsApi(searchForm: Form<PatientSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'patients/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'patient', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePostPatientApi(): ApiSet<BaseResponse> & { execute: (form: Form<PatientForm>) => void } {
    const apiSet = usePostApi<BaseResponse, PatientForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<PatientForm>) => {
        const apiPath = `patients/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutPatientApi(): ApiSet<BaseResponse> & { execute: (form: Form<PatientForm>) => void } {
    const apiSet = usePutApi<BaseResponse, PatientForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<PatientForm>) => {
        const apiPath = `patients/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutPatientsApi(): ApiSet<BaseResponse> & { execute: (form: Form<PatientForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, PatientForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<PatientForm[]>) => {
        const apiPath = `patients/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeletePatientApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `patients/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
